import React, {
  useCallback,
  useEffect,
  useRef,
} from 'react'

import PropTypes from 'prop-types'
import classnames from 'classnames'

import Styles from './styles.module.sass'


// The `handleClick` prop is now improperly named because it's
// actually invoked `onChange` instead, but since there's no material
// difference between the behaviors of the two events in the context
// of a checkbox, I'm just going to let sleeping dogs lie.
//
/**
 * N.B.: **This is a controlled component**: it does not maintain its own state,
 * so clicking on the checkbox will do nothing more than invoke the handleClick
 * callback.
 *
 * @param {Object} props
 *
 * @returns {Node}
 */
export default function Checkbox(props) {
  const {
    'aria-label': ariaLabel,
    checked,
    className,
    disabled,
    handleClick,
    indeterminate,
    label,
    name,
    value,
  } = props

  const ref = useRef()

  useEffect(() => {
    if ( !ref.current ) {
      return
    }

    ref.current.indeterminate = indeterminate
  }, [indeterminate])

  const onClick = useCallback((e) => {
    if ( disabled ) {
      return
    }

    e.stopPropagation()

    if ( ref.current ) {
      ref.current.indeterminate = false
    }

    handleClick({
      checked: !checked,
      name,
      value,
    })
  }, [disabled, handleClick, name, value, checked])

  const onKeyUp = useCallback((e) => {
    if ( e.key !== ' ' ) {
      return
    }

    onClick(e)
  }, [onClick])

  const rootClassName = classnames(Styles.root, className, {
    [Styles.disabled]: disabled,
  })

  const cbClassName = classnames(Styles.checkbox, {
    [Styles.checked]: ( checked && !indeterminate ),
    [Styles.indeterminate]: indeterminate,
  })

  const ariaChecked = ( indeterminate ) ? 'mixed' : checked
  const tabIndex = ( disabled ) ? -1 : 0

  return (
    <label
      aria-checked={ ariaChecked }
      aria-disabled={ disabled }
      aria-label={ ariaLabel }
      className={ rootClassName }
      onKeyUp={ onKeyUp }
      role="checkbox"
      tabIndex={ tabIndex }
    >
      {
        // The <input> is provided to make it easier to query state for
        // automated testing, to make this component more like
        // RadioButton, and to facilitate the use in the context of a
        // <form> element. But, it's hidden from view because styling
        // checkboxes is nigh onto impossible.
      }
      <input
        aria-hidden="true"
        checked={ checked }
        className={ Styles.input }
        disabled={ disabled }
        name={ name }
        onChange={ onClick }
        ref={ ref }
        type="checkbox"
        value={ value }
      />
      <div
        className={ cbClassName }
        data-testid="cb-ui"
      />
      <span className={ Styles.label }>
        { label }
      </span>
    </label>
  )
}

Checkbox.propTypes = {
  /** invoked with `{ name, value, checked }` */
  'handleClick': PropTypes.func.isRequired,

  'aria-label': PropTypes.string,
  'label': PropTypes.node,
  'checked': PropTypes.bool,
  'className': PropTypes.string,
  'disabled': PropTypes.bool,
  'indeterminate': PropTypes.bool,
  'name': PropTypes.string,
  'value': PropTypes.any,
}
